/**
 * WeGotNuts Theme - Global Vacation / Checkout Pause Mode Controller
 * Accessible, responsive, state-aware, zero-dependency Vanilla JS module.
 *
 * Responsibilities:
 * 1. Checkout Interception: Intercepts all normal storefront checkout entry points
 *    (buttons, links, form submits) in the capture phase.
 * 2. Add-to-Cart Pause Enforcement: When settings.vacation_allow_add_to_cart is FALSE,
 *    intercepts all product add actions globally (Voomy main, sticky bar, PDP, quick view,
 *    product cards, custom tray builders, drawer quantity plus) and halts network requests.
 * 3. Dual-Context Modal: Displays context-aware notice:
 *    - Context "checkout": Explains order pause, reassures cart safety.
 *    - Context "add-to-cart": Explains ordering pause, directs to collections.
 * 4. Existing Cart Protection: Preserves existing carts; allows line decrements and removals.
 * 5. Defense-in-Depth: Transparent surgical fetch/ajax prefilter guards ensure zero /cart/add*
 *    network requests leave the browser when Add to Cart is disabled.
 */
(function () {
  'use strict';

  var WgnVacationMode = {
    modal: null,
    dialog: null,
    lastActiveElement: null,
    isOpen: false,
    _closeTimer: null,
    _initialized: false,
    _originalFetch: null,
    _fetchHooked: false,
    _ajaxHooked: false,

    /**
     * Resolves the runtime Vacation Mode configuration.
     */
    getConfig: function () {
      var globalCfg = window.WgnVacationConfig || {};
      var modalEl = this.modal || document.getElementById('WgnVacationModal');
      var allowAtcAttr = modalEl ? modalEl.getAttribute('data-allow-atc') : null;

      var allowAtc = true;
      if (typeof globalCfg.allowAddToCart === 'boolean') {
        allowAtc = globalCfg.allowAddToCart;
      } else if (allowAtcAttr !== null) {
        allowAtc = (allowAtcAttr === 'true');
      } else if (document.body.classList.contains('wgn-vacation-atc-disabled')) {
        allowAtc = false;
      }

      return {
        enabled: (typeof globalCfg.enabled === 'boolean') ? globalCfg.enabled : document.body.classList.contains('wgn-vacation-mode'),
        allowAddToCart: allowAtc
      };
    },

    /**
     * Returns true if Add to Cart is globally paused.
     */
    isAddToCartBlocked: function () {
      var cfg = this.getConfig();
      return cfg.enabled && !cfg.allowAddToCart;
    },

    init: function () {
      this.modal = document.getElementById('WgnVacationModal');
      if (!this.modal) return;

      this.dialog = this.modal.querySelector('.wgn-vacation-modal__dialog');

      if (!this._initialized) {
        this.bindGlobalInterception();
        this.bindModalEvents();
        this.installNetworkGuards();
        this.enhanceCartPage();
        this.enhanceAddToCartControls();
        this.checkAutoOpenSession();
        this._initialized = true;
      } else {
        this.enhanceCartPage();
        this.enhanceAddToCartControls();
      }
    },

    /**
     * Determines whether a given DOM element represents a checkout action.
     */
    isCheckoutTrigger: function (element) {
      if (!element || element.nodeType !== 1) return false;

      var el = element;
      var depth = 0;

      while (el && el !== document.body && depth < 5) {
        if (el.getAttribute) {
          var name = el.getAttribute('name');
          if (name === 'checkout' || name === 'goto_checkout') {
            return true;
          }
        }

        if (el.tagName === 'A') {
          var href = el.getAttribute('href') || '';
          if (href === '/checkout' || href.indexOf('/checkout') === 0 || href.indexOf('wegotnuts.com/checkout') !== -1) {
            return true;
          }
        }

        if (el.classList) {
          if (
            el.classList.contains('cart__checkout') ||
            el.classList.contains('checkout-btn') ||
            el.classList.contains('checkout_btn') ||
            el.classList.contains('cart-checkout-btn') ||
            el.classList.contains('icart-checkout-btn') ||
            el.classList.contains('shopify-payment-button__button')
          ) {
            return true;
          }

          if (el.closest && el.closest('.proceed-to-checkout') && (el.tagName === 'BUTTON' || el.tagName === 'A' || el.getAttribute('type') === 'submit')) {
            return true;
          }
        }

        el = el.parentElement;
        depth++;
      }

      return false;
    },

    /**
     * Determines whether a given DOM element represents an Add to Cart action.
     * Accurately matches PDP buttons, sticky bar, cards, quick view, tray builder, and drawer quantity increases.
     * Explicitly ignores quantity decrement, item removal, cart update, variant selects, and search/filters.
     */
    isAddToCartTrigger: function (element) {
      if (!element || element.nodeType !== 1) return false;

      var el = element;
      var depth = 0;

      while (el && el !== document.body && depth < 5) {
        // Explicitly ignore decrement / removal / cart update
        if (el.classList) {
          if (
            el.classList.contains('ajaxcart__qty--minus') ||
            el.classList.contains('pro-remove') ||
            el.classList.contains('cart_remove') ||
            el.classList.contains('wgn-vacation-modal__close') ||
            el.hasAttribute('data-wgn-vacation-close')
          ) {
            return false;
          }
        }

        if (el.getAttribute) {
          var name = el.getAttribute('name');
          if (name === 'add') return true;
          if (name === 'update') return false; // Handled separately for quantity increase check

          if (el.getAttribute('data-wgn-vacation-add-blocked') === 'true') return true;
          if (el.hasAttribute('data-atc')) return true; // Custom Tray Builder button
        }

        // Cart Drawer quantity increase button
        if (el.classList && el.classList.contains('ajaxcart__qty--plus')) {
          return true;
        }

        // Voomy sticky bar or forms targeting /cart/add
        if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
          var formId = el.getAttribute('form');
          if (formId) {
            var associatedForm = document.getElementById(formId);
            if (associatedForm) {
              var formAction = associatedForm.getAttribute('action') || '';
              if (formAction.indexOf('/cart/add') !== -1) return true;
            }
          }

          var parentForm = el.closest ? el.closest('form') : null;
          if (parentForm) {
            var action = parentForm.getAttribute('action') || '';
            if (action.indexOf('/cart/add') !== -1 && (el.type === 'submit' || el.name === 'add')) {
              return true;
            }
          }
        }

        // Known theme classes
        if (el.classList) {
          if (
            el.classList.contains('wgn-atc-pill__submit') ||
            el.classList.contains('wgn-premium-card__btn') ||
            el.classList.contains('addtocartqv') ||
            el.classList.contains('ajax-spin-cart') ||
            el.id === 'AddToCart'
          ) {
            return true;
          }

          if (el.closest && el.closest('.product-quick-add') && (el.tagName === 'BUTTON' || el.getAttribute('type') === 'submit')) {
            return true;
          }
        }

        // Links with inline ajaxCart.add(...)
        if (el.tagName === 'A') {
          var onclick = el.getAttribute('onclick') || '';
          if (onclick.indexOf('ajaxCart.add') !== -1 || onclick.indexOf('Shopify.addItem') !== -1) {
            return true;
          }
        }

        el = el.parentElement;
        depth++;
      }

      return false;
    },

    /**
     * Intercepts a checkout attempt and displays the modal in checkout context.
     */
    blockCheckoutAttempt: function (e, triggerEl) {
      if (e) {
        if (typeof e.preventDefault === 'function') e.preventDefault();
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }

      this.open(triggerEl || (e ? e.target : null), 'checkout');
      return false;
    },

    /**
     * Intercepts an Add-to-Cart attempt and displays the modal in add-to-cart context.
     */
    blockAddToCartAttempt: function (e, triggerEl) {
      if (e) {
        if (typeof e.preventDefault === 'function') e.preventDefault();
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      }

      this.open(triggerEl || (e ? e.target : null), 'add-to-cart');
      return false;
    },

    /**
     * Binds capturing event delegation for click and submit events sitewide.
     */
    bindGlobalInterception: function () {
      var self = this;

      // 1. Delegated Click Interception in Capture Phase
      window.addEventListener(
        'click',
        function (e) {
          var target = e.target;

          // Check Checkout triggers first
          if (self.isCheckoutTrigger(target)) {
            self.blockCheckoutAttempt(e, target);
            return;
          }

          // Check Add-to-Cart triggers if ATC is disabled
          if (self.isAddToCartBlocked() && self.isAddToCartTrigger(target)) {
            self.blockAddToCartAttempt(e, target);
          }
        },
        true // Capture phase: executes before all element listeners, jQuery, and custom elements
      );

      // 2. Delegated Submit Interception in Capture Phase
      window.addEventListener(
        'submit',
        function (e) {
          var form = e.target;
          if (!form || form.tagName !== 'FORM') return;

          var action = form.getAttribute('action') || '';
          var isAddForm = (action.indexOf('/cart/add') !== -1);
          var isCartForm = (action === '/cart' || action.indexOf('/cart?') === 0 || form.classList.contains('cart') || form.classList.contains('ajaxcart'));

          // Case A: Product Add-to-Cart Form
          if (isAddForm) {
            if (self.isAddToCartBlocked()) {
              self.blockAddToCartAttempt(e, form);
            }
            return;
          }

          // Case B: Cart Page / Drawer Form
          if (isCartForm) {
            var submitter = e.submitter;

            // Check if user clicked "Update Cart"
            if (submitter && submitter.getAttribute('name') === 'update') {
              // If ATC is blocked, inspect whether any line item quantity was INCREASED
              if (self.isAddToCartBlocked()) {
                var qtyInputs = form.querySelectorAll('input[name="updates[]"]');
                var hasQuantityIncrease = false;

                for (var i = 0; i < qtyInputs.length; i++) {
                  var inp = qtyInputs[i];
                  var currentVal = parseInt(inp.value, 10) || 0;
                  var initialVal = parseInt(inp.getAttribute('data-initial-qty'), 10);
                  if (isNaN(initialVal)) initialVal = currentVal;

                  if (currentVal > initialVal) {
                    hasQuantityIncrease = true;
                    // Reset to initial quantity to prevent unexpected mutation
                    inp.value = initialVal;
                  }
                }

                if (hasQuantityIncrease) {
                  self.blockAddToCartAttempt(e, submitter);
                  return;
                }
              }
              // Allow normal quantity decreases or removals
              return;
            }

            // If submitter was checkout button or Enter key pressed
            if (!submitter || submitter.getAttribute('name') === 'checkout' || self.isCheckoutTrigger(submitter)) {
              self.blockCheckoutAttempt(e, submitter || form);
            }
          }
        },
        true // Capture phase
      );

      // 3. Document-level keyboard support (ESC & Focus Trap)
      document.addEventListener('keydown', function (e) {
        if (!self.isOpen) return;

        var isEscape = e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
        var isTab    = e.key === 'Tab'    || e.keyCode === 9;

        if (isEscape) {
          e.preventDefault();
          self.close();
        } else if (isTab) {
          self.trapFocus(e);
        }
      });
    },

    /**
     * Installs surgical defense-in-depth network guards for direct fetch and jQuery.ajax calls.
     * Guarantees 0 POST requests to /cart/add* can succeed when Add to Cart is paused.
     */
    installNetworkGuards: function () {
      var self = this;

      // Guard window.fetch
      if (window.fetch && !this._fetchHooked) {
        this._originalFetch = window.fetch;
        window.fetch = function (resource, init) {
          var url = '';
          if (typeof resource === 'string') {
            url = resource;
          } else if (resource && resource.url) {
            url = resource.url;
          }

          var isCartAddUrl = (url.indexOf('/cart/add') !== -1);

          if (isCartAddUrl && self.isAddToCartBlocked()) {
            self.blockAddToCartAttempt(null, null);
            return Promise.reject(new Error('Ordering is temporarily paused.'));
          }

          return self._originalFetch.apply(window, arguments);
        };
        this._fetchHooked = true;
      }

      // Guard jQuery.ajaxPrefilter
      if (window.jQuery && typeof window.jQuery.ajaxPrefilter === 'function' && !this._ajaxHooked) {
        window.jQuery.ajaxPrefilter(function (options, originalOptions, jqXHR) {
          if (options && options.url && options.url.indexOf('/cart/add') !== -1 && self.isAddToCartBlocked()) {
            jqXHR.abort();
            self.blockAddToCartAttempt(null, null);
          }
        });
        this._ajaxHooked = true;
      }
    },

    /**
     * Binds close triggers on the modal elements.
     */
    bindModalEvents: function () {
      var self = this;

      this.modal.addEventListener('click', function (e) {
        var closeTrigger = e.target.closest('[data-wgn-vacation-close]');
        if (closeTrigger) {
          e.preventDefault();
          self.close();
        }
      });
    },

    /**
     * Opens the Vacation modal in the specified context ('checkout' or 'add-to-cart').
     */
    open: function (triggerElement, context) {
      if (this.isOpen) return;

      var mode = context;
      if (!mode) {
        mode = this.isAddToCartTrigger(triggerElement) ? 'add-to-cart' : 'checkout';
      }

      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }

      this.lastActiveElement = triggerElement || document.activeElement;
      this.isOpen = true;

      // 1. Set mode attribute for dual-context styling and accessibility
      this.modal.setAttribute('data-mode', mode);

      var titleId = (mode === 'add-to-cart') ? 'WgnVacationModalTitleAtc' : 'WgnVacationModalTitleCheckout';
      var descId  = (mode === 'add-to-cart') ? 'WgnVacationModalDescAtc'  : 'WgnVacationModalDescCheckout';

      this.modal.setAttribute('aria-labelledby', titleId);
      this.modal.setAttribute('aria-describedby', descId);

      // 2. Remove hidden attribute to enter render tree
      this.modal.removeAttribute('hidden');

      // 3. Force reflow
      // eslint-disable-next-line no-unused-expressions
      this.modal.offsetHeight;

      // 4. Apply active state
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');

      // 5. Lock background scroll safely
      document.body.classList.add('wgn-vacation-modal-open');

      // 6. Shift focus to primary action button
      var focusTarget = this.modal.querySelector('#WgnVacationBtnContinue') ||
                        this.modal.querySelector('#WgnVacationModalClose');
      if (focusTarget) {
        setTimeout(function () {
          focusTarget.focus();
        }, 60);
      }
    },

    /**
     * Closes the Vacation modal and safely restores focus and background scroll.
     */
    close: function () {
      if (!this.isOpen) return;

      var self = this;
      this.isOpen = false;

      // 1. Remove active state to trigger closing transition
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');

      // 2. Remove body scroll lock
      document.body.classList.remove('wgn-vacation-modal-open');

      // 3. Restore focus to previous active element
      if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
        try {
          this.lastActiveElement.focus();
        } catch (err) {}
      }

      // 4. Transition cleanup
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        self.modal.setAttribute('hidden', '');
        return;
      }

      var handled = false;
      var transitionHandler = function (e) {
        if (e.target !== self.modal) return;
        if (handled) return;
        handled = true;
        self.modal.removeEventListener('transitionend', transitionHandler);
        clearTimeout(self._closeTimer);
        self._closeTimer = null;
        if (!self.isOpen) {
          self.modal.setAttribute('hidden', '');
        }
      };

      this.modal.addEventListener('transitionend', transitionHandler);

      this._closeTimer = setTimeout(function () {
        if (handled) return;
        self.modal.removeEventListener('transitionend', transitionHandler);
        self._closeTimer = null;
        if (!self.isOpen) {
          self.modal.setAttribute('hidden', '');
        }
      }, 320);
    },

    /**
     * Traps keyboard focus within the modal dialog.
     */
    trapFocus: function (e) {
      if (!this.modal) return;

      var focusables = this.modal.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusables || !focusables.length) return;

      var first = focusables[0];
      var last  = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },

    /**
     * Enhances cart page elements: adds pause notices and records initial quantities for increase checking.
     */
    enhanceCartPage: function () {
      // 1. Checkout buttons enhancement
      var checkoutBtns = document.querySelectorAll('.proceed-to-checkout button[name="checkout"], .proceed-to-checkout .theme-default-button');
      for (var i = 0; i < checkoutBtns.length; i++) {
        var btn = checkoutBtns[i];
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('title', 'Checkout is temporarily paused while we are away.');

        var parent = btn.parentElement;
        if (parent && !parent.querySelector('.wgn-cart-vacation-notice')) {
          var notice = document.createElement('div');
          notice.className = 'wgn-cart-vacation-notice';
          notice.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg> <span>Checkout temporarily paused &bull; Reopening Oct 1</span>';
          parent.appendChild(notice);
        }
      }

      // 2. Quantity inputs initial-value tracking (for Add-to-Cart paused enforcement)
      var qtyInputs = document.querySelectorAll('input[name="updates[]"]');
      for (var j = 0; j < qtyInputs.length; j++) {
        var input = qtyInputs[j];
        if (!input.hasAttribute('data-initial-qty')) {
          input.setAttribute('data-initial-qty', input.value || '0');
        }
      }
    },

    /**
     * Enhances Add to Cart controls across all surfaces when Add to Cart is disabled.
     */
    enhanceAddToCartControls: function () {
      if (!this.isAddToCartBlocked()) return;

      var pausedText = 'Ordering Temporarily Paused';
      var pausedTitle = 'Ordering is temporarily paused while we are away.';

      // Enhance PDP & Card ATC buttons
      var atcButtons = document.querySelectorAll('button[name="add"], .wgn-atc-pill__submit, .wgn-premium-card__btn, #AddToCart, .addtocartqv, [data-atc]');
      for (var i = 0; i < atcButtons.length; i++) {
        var b = atcButtons[i];
        b.setAttribute('aria-disabled', 'true');
        b.setAttribute('title', pausedTitle);

        // Update text node if it matches standard Add to Cart labels
        var textEl = b.querySelector('.wgn-atc-pill__atc-text, .cart-title, span, [data-atc-text]') || b;
        if (textEl && textEl.textContent) {
          var t = textEl.textContent.trim().toLowerCase();
          if (t === 'add to cart' || t === 'add custom tray to cart' || t === 'quick add') {
            textEl.textContent = pausedText;
          }
        }
      }

      // Watch Tray Builder button state dynamically
      var trayAtc = document.querySelector('[data-atc]');
      if (trayAtc && !trayAtc._wgnObserved) {
        var observer = new MutationObserver(function () {
          var span = trayAtc.querySelector('[data-atc-text]');
          if (span && span.textContent && span.textContent.trim().toLowerCase() === 'add custom tray to cart') {
            span.textContent = pausedText;
          }
        });
        observer.observe(trayAtc, { childList: true, subtree: true, characterData: true });
        trayAtc._wgnObserved = true;
      }
    },

    /**
     * Automatically displays the vacation notice once per browser session.
     * Reassures the customer upon initial store visit while preventing repetitive popups on page navigation.
     */
    checkAutoOpenSession: function () {
      var cfg = this.getConfig();
      if (!cfg.enabled) return;

      var SESSION_KEY = 'wgn_vacation_popup_seen';
      var alreadySeen = false;

      try {
        if (window.sessionStorage) {
          alreadySeen = (window.sessionStorage.getItem(SESSION_KEY) === 'true');
        }
      } catch (err) {
        alreadySeen = false;
      }

      if (alreadySeen) return;

      // Mark as seen immediately for this session
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(SESSION_KEY, 'true');
        }
      } catch (err) {}

      var self = this;
      var context = cfg.allowAddToCart ? 'checkout' : 'add-to-cart';

      // Slight delay for smooth initial page render before dialog presentation
      setTimeout(function () {
        if (!self.isOpen) {
          self.open(null, context);
        }
      }, 700);
    }
  };

  // ----- Boot ----------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      WgnVacationMode.init();
    });
  } else {
    WgnVacationMode.init();
  }

  // Shopify Theme Editor support
  document.addEventListener('shopify:section:load', function () {
    WgnVacationMode.init();
  });

  // Expose global controller
  window.WgnVacationMode = WgnVacationMode;

})();
